import type { OutputMode } from "@remote-agents/shared";
import { useEffect, useRef, useState } from "react";

type Props = {
  condensedLog: string;
  fullLog: string;
  summary: string;
  outputMode: OutputMode;
  onToggle: (mode: OutputMode) => void;
  toggleBusy?: boolean;
  pinnedPrompt: string | null;
  running: boolean;
  lastActions?: string[];
  stale?: boolean;
  lastUpdateAt?: number;
  now?: number;
};

export function ActivityFeed({
  condensedLog,
  fullLog,
  summary,
  outputMode,
  onToggle,
  toggleBusy,
  pinnedPrompt,
  running,
  lastActions = [],
  stale = false,
  lastUpdateAt,
  now = Date.now(),
}: Props) {
  const boxRef = useRef<HTMLPreElement>(null);
  const [follow, setFollow] = useState(true);
  const [pausedByUser, setPausedByUser] = useState(false);

  const log =
    outputMode === "full"
      ? fullLog || condensedLog
      : condensedLog || summary || fullLog;

  useEffect(() => {
    const el = boxRef.current;
    if (el && follow && !pausedByUser) {
      el.scrollTop = el.scrollHeight;
    }
  }, [log, follow, outputMode, pausedByUser]);

  function onScroll() {
    const el = boxRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom < 48;
    if (!nearBottom && follow) {
      setPausedByUser(true);
      setFollow(false);
      return;
    }
    if (nearBottom && !follow) {
      setPausedByUser(false);
      setFollow(true);
    }
  }

  function jumpToLatest() {
    const el = boxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setPausedByUser(false);
    setFollow(true);
  }

  function pauseFollow() {
    setPausedByUser(true);
    setFollow(false);
  }

  const emptyMessage = running
    ? "Waiting for the first stream update…"
    : "No activity yet. Send an instruction to get started.";

  return (
    <section className="card feed">
      <div className="feed__head">
        <h2>Live activity</h2>
        <div className="feed__head-actions">
          {follow ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={pauseFollow}
              aria-pressed={false}
            >
              Pause scroll
            </button>
          ) : null}
          <div
            className="segment segment--sm"
            role="group"
            aria-label="Log detail"
          >
            <button
              type="button"
              className={outputMode === "condensed" ? "is-on" : ""}
              onClick={() => onToggle("condensed")}
              disabled={toggleBusy}
            >
              Condensed
            </button>
            <button
              type="button"
              className={outputMode === "full" ? "is-on" : ""}
              onClick={() => onToggle("full")}
              disabled={toggleBusy}
            >
              Full
            </button>
          </div>
        </div>
      </div>

      {stale && running ? (
        <p className="banner banner--warn feed__stale" role="status">
          Stream quiet — last update{" "}
          {lastUpdateAt
            ? `${Math.max(1, Math.floor((now - lastUpdateAt) / 1000))}s ago`
            : "a while ago"}
          . The run may still be active on the worker.
        </p>
      ) : null}

      {pinnedPrompt && running ? (
        <div className="feed__pinned">
          <span className="feed__pinned-label">Working on</span>
          <p className="feed__pinned-text">{pinnedPrompt}</p>
        </div>
      ) : null}

      {lastActions.length > 0 ? (
        <div className="tool-trail feed__tools" aria-label="Recent tools">
          {lastActions.map((action, index) => (
            <span key={`${action}-${index}`} className="chip tool-trail__chip">
              {action}
            </span>
          ))}
        </div>
      ) : null}

      <div className="feed__body">
        <pre className="log feed__log" ref={boxRef} onScroll={onScroll}>
          {log || emptyMessage}
        </pre>
        {!follow ? (
          <div className="feed__jump-row">
            <button type="button" className="feed__jump" onClick={jumpToLatest}>
              ↓ Jump to latest
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
